import React from 'react';
import { Link } from 'react-router-dom';
import {
  Rocket, Play, Star, ChevronRight, Trophy, Download, GraduationCap,
  Shield, ScrollText, Wallet, Mail, MessageCircle, Sparkles, BookOpen,
  School, Library, FileQuestion, FileText, BarChart3, CalendarDays,
  Bell, TrendingUp, FileDown, Bookmark, LineChart, Brain, Timer, Flame,
  LogIn,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  brand, stats, mainSections, testimonials, upcomingEvents,
  navodayaFeatures, teacherFeatures, advancedFeatures,
} from '../mock';
import { useStreak, useProgress, useBookmarks } from '../hooks/useLocal';
import { useAuth } from '../context/AuthContext';
import { usePlans } from '../hooks/usePlans';

const iconMap = {
  GraduationCap, ScrollText, BookOpen, School, Library, FileQuestion,
  FileText, BarChart3, CalendarDays, Star, FileDown, Bookmark, LineChart,
  Brain, Timer, Flame, Trophy, MessageCircle, Bell,
};

export default function HomePage() {
  const { streak } = useStreak();
  const { readCount } = useProgress();
  const { bookmarks } = useBookmarks();
  const { user, login } = useAuth();
  const { minPrice } = usePlans();
  return (
    <div className="px-4 pt-4 space-y-5">
      {/* Hero Card - Distinctive Gurusevakendra layout with split design */}
      <div className="relative overflow-hidden rounded-3xl shadow-lg">
        {/* Top band with brand */}
        <div className="relative px-5 pt-5 pb-4 text-white" style={{ background: 'linear-gradient(115deg, #047857 0%, #0d9488 40%, #0e7490 80%, #1e40af 100%)' }}>
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, #fbbf24 0%, transparent 40%), radial-gradient(circle at 80% 90%, #06b6d4 0%, transparent 40%)' }} />
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="relative">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center shadow-md rotate-3">
                  <GraduationCap size={22} className="text-emerald-900" />
                </div>
                <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
                  <Sparkles size={10} className="text-white" />
                </div>
              </div>
              <div>
                <div className="font-extrabold text-lg leading-tight" style={{ letterSpacing: '-0.02em' }}>{brand.name}</div>
                <div className="text-[10px] text-white/85 tracking-wider uppercase">{brand.nameOdia}</div>
              </div>
            </div>
            <Link to="/notifications" className="w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-white/25 relative">
              <Bell size={16} />
              <span className="absolute top-1 right-1.5 w-2 h-2 rounded-full bg-amber-400" />
            </Link>
          </div>

          <h1 className="mt-5 text-2xl font-extrabold leading-[1.1]">
            {brand.name}
          </h1>
          <p className="text-sm mt-2 text-amber-200 font-bold">{brand.tagline}</p>
          <p className="text-[11px] mt-0.5 text-white/75">{brand.taglineEn} · Bilingual ଓଡ଼ିଆ + English</p>
        </div>

        {/* Bottom action band */}
        <div className="bg-white px-5 py-4 border-t-4 border-amber-400">
          <div className="flex gap-2">
            <Link to="/navodaya" className="flex-1 h-11 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm flex items-center justify-center gap-1.5 shadow-md">
              <Rocket size={15} /> ଆରମ୍ଭ କରନ୍ତୁ
            </Link>
            <Link to="/quiz" className="h-11 px-4 rounded-xl border-2 border-emerald-600 text-emerald-700 font-bold text-sm flex items-center gap-1.5">
              <Brain size={15} /> Quiz
            </Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <Card className="grid grid-cols-4 divide-x divide-gray-200 py-3 rounded-2xl border-gray-200">
        {stats.map((s) => (
          <div key={s.label} className="text-center px-1">
            <div className="text-base font-extrabold text-emerald-600">{s.value}</div>
            <div className="text-[9px] text-gray-500 font-medium mt-0.5 leading-tight">{s.label}</div>
          </div>
        ))}
      </Card>

      {/* Login prompt for guests */}
      {!user && (
        <Card className="rounded-2xl p-4 border-emerald-200 bg-emerald-50 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center"><LogIn size={18} /></div>
          <div className="flex-1">
            <div className="font-bold text-sm text-emerald-900">Google ସହ ଲଗଇନ କରନ୍ତୁ</div>
            <div className="text-[11px] text-emerald-700">Quiz score, Bookmarks ଓ AI Chat ପାଇଁ ଆବଶ୍ୟକ</div>
          </div>
          <button onClick={login} className="bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-full">Login</button>
        </Card>
      )}

      {/* Personal Progress Card */}
      <div className="grid grid-cols-3 gap-2.5">
        <Link to="/bookmarks" className="rounded-2xl p-3 border border-gray-200 bg-white text-center hover:border-orange-300 transition-colors">
          <Flame size={18} className="mx-auto text-orange-500" />
          <div className="text-base font-extrabold text-gray-900 mt-1">{streak}</div>
          <div className="text-[9px] text-gray-500 leading-tight">ଷ୍ଟ୍ରିକ · Streak</div>
        </Link>
        <Link to="/bookmarks" className="rounded-2xl p-3 border border-gray-200 bg-white text-center hover:border-emerald-300 transition-colors">
          <TrendingUp size={18} className="mx-auto text-emerald-500" />
          <div className="text-base font-extrabold text-gray-900 mt-1">{readCount}</div>
          <div className="text-[9px] text-gray-500 leading-tight">ପଠିତ · Read</div>
        </Link>
        <Link to="/bookmarks" className="rounded-2xl p-3 border border-gray-200 bg-white text-center hover:border-amber-300 transition-colors">
          <Bookmark size={18} className="mx-auto text-amber-500" />
          <div className="text-base font-extrabold text-gray-900 mt-1">{bookmarks.length}</div>
          <div className="text-[9px] text-gray-500 leading-tight">ଚିହ୍ନିତ · Saved</div>
        </Link>
      </div>

      {/* Main Sections */}
      <div>
        <h2 className="text-lg font-extrabold text-gray-900 flex items-center gap-1.5">
          <Sparkles size={16} className="text-emerald-500" /> ମୁଖ୍ୟ ବିଭାଗ · Main Sections
        </h2>
        <div className="space-y-3 mt-3">
          {mainSections.map((s) => {
            const Icon = iconMap[s.icon] || BookOpen;
            return (
              <Link key={s.id} to={s.route}
                className={`relative overflow-hidden rounded-2xl p-4 text-white shadow-md bg-gradient-to-br ${s.gradient} block hover:scale-[1.02] active:scale-[0.99] transition-transform`}>
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-white/10 rounded-full blur-xl" />
                {s.badge && (
                  <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/25 backdrop-blur">{s.badge}</span>
                )}
                <div className="relative flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center border border-white/25 shrink-0">
                    <Icon size={22} />
                  </div>
                  <div className="flex-1">
                    <div className="font-extrabold text-base leading-tight">{s.title}</div>
                    <div className="text-[11px] text-white/85 font-medium">{s.titleEn}</div>
                    <div className="text-[11px] text-white/90 mt-1">{s.subtitle}</div>
                  </div>
                  <ChevronRight size={20} className="opacity-80" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Advanced Features */}
      <div>
        <h2 className="text-lg font-extrabold text-gray-900 flex items-center gap-1.5">
          <Sparkles size={16} className="text-emerald-500" /> ଉନ୍ନତ ସୁବିଧା · Advanced Features
        </h2>
        <div className="grid grid-cols-3 gap-2.5 mt-3">
          {[
            { id: 'quiz', title: 'Interactive Quiz', titleEn: 'Timed + Scoring', icon: 'Brain', gradient: 'from-fuchsia-500 to-pink-600', to: '/quiz' },
            { id: 'chat', title: 'AI ସହାୟକ', titleEn: 'Doubt Solving', icon: 'MessageCircle', gradient: 'from-violet-500 to-purple-600', to: '/chat' },
            { id: 'leaderboard', title: 'Leaderboard', titleEn: 'Top Scorers', icon: 'Trophy', gradient: 'from-amber-500 to-orange-600', to: '/leaderboard' },
            { id: 'resources', title: 'ଅଫିସିଆଲ ପୁସ୍ତକ', titleEn: 'NCERT/BSE Links', icon: 'Library', gradient: 'from-emerald-500 to-teal-600', to: '/resources' },
            { id: 'notifications', title: 'ସୂଚନା', titleEn: 'Notifications', icon: 'Bell', gradient: 'from-cyan-500 to-blue-600', to: '/notifications' },
            { id: 'download-a4', title: 'A4 PDF', titleEn: 'Download', icon: 'FileDown', gradient: 'from-blue-500 to-indigo-600', to: '/notes/board' },
          ].map((f) => {
            const Icon = iconMap[f.icon] || FileText;
            return (
              <Link key={f.id} to={f.to}
                className={`relative overflow-hidden rounded-2xl p-3 text-white shadow-sm bg-gradient-to-br ${f.gradient} min-h-[100px] flex flex-col justify-between hover:scale-105 active:scale-95 transition-transform`}>
                <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                  <Icon size={16} />
                </div>
                <div>
                  <div className="font-bold text-[11px] leading-tight">{f.title}</div>
                  <div className="text-[9px] text-white/85">{f.titleEn}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Upcoming Events */}
      <Card className="rounded-2xl p-4 border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <Bell size={16} className="text-orange-500" />
          <h3 className="font-bold text-gray-900">ଆଗାମୀ ଘଟଣା · Upcoming</h3>
        </div>
        <div className="space-y-2">
          {upcomingEvents.map((e, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl bg-gray-50">
              <div className={`text-[10px] font-bold px-2 py-1 rounded-md ${e.color}`}>{e.tag}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-xs text-gray-900 truncate">{e.titleOdia}</div>
                <div className="text-[10px] text-gray-500 truncate">{e.title}</div>
              </div>
              <div className="text-[11px] font-semibold text-gray-700 shrink-0">{e.date}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* NEW: AI-Powered Tools & Curriculum */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} className="text-purple-600" />
          <h2 className="text-lg font-extrabold text-gray-900">🚀 New · AI Tools & Curriculum</h2>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <Link to="/curriculum" data-testid="home-curriculum" className="rounded-xl p-3 text-white shadow-md bg-gradient-to-br from-emerald-500 to-teal-600 hover:scale-105 active:scale-95 transition-transform min-h-[100px] flex flex-col justify-between">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center"><GraduationCap size={18} /></div>
            <div>
              <div className="font-extrabold text-sm">Curriculum</div>
              <div className="text-[10px] text-white/85">Class 1-10 · DG/CG/LO</div>
            </div>
          </Link>
          <Link to="/lesson-plan" data-testid="home-lesson-plan" className="rounded-xl p-3 text-white shadow-md bg-gradient-to-br from-purple-500 to-pink-600 hover:scale-105 active:scale-95 transition-transform min-h-[100px] flex flex-col justify-between">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center"><Sparkles size={18} /></div>
            <div>
              <div className="font-extrabold text-sm">AI Lesson Plan</div>
              <div className="text-[10px] text-white/85">Full & Short · Odia</div>
            </div>
          </Link>
          <Link to="/sources" data-testid="home-sources" className="rounded-xl p-3 text-white shadow-md bg-gradient-to-br from-cyan-500 to-blue-600 hover:scale-105 active:scale-95 transition-transform min-h-[100px] flex flex-col justify-between">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center"><Library size={18} /></div>
            <div>
              <div className="font-extrabold text-sm">Official Sources</div>
              <div className="text-[10px] text-white/85">SCERT · BSE · NVS · NCERT</div>
            </div>
          </Link>
          <Link to="/navodaya/practice" data-testid="home-nav-practice" className="rounded-xl p-3 text-white shadow-md bg-gradient-to-br from-orange-500 to-red-600 hover:scale-105 active:scale-95 transition-transform min-h-[100px] flex flex-col justify-between">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center"><Rocket size={18} /></div>
            <div>
              <div className="font-extrabold text-sm">Navodaya AI Practice</div>
              <div className="text-[10px] text-white/85">Class 6 & 9 · AI Questions</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Quick Access - Navodaya Features */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-gray-900">ନବୋଦୟ ପ୍ରସ୍ତୁତି</h2>
          <Link to="/navodaya" className="text-xs font-semibold text-emerald-600 flex items-center gap-0.5">
            ସବୁ ଦେଖନ୍ତୁ <ChevronRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-2.5 mt-3">
          {navodayaFeatures.slice(0, 6).map((f, i) => {
            const Icon = iconMap[f.icon] || FileText;
            return (
              <Link key={i} to="/navodaya"
                className={`relative overflow-hidden rounded-xl p-3 text-white shadow-sm bg-gradient-to-br ${f.gradient} min-h-[95px] flex flex-col justify-between hover:scale-105 active:scale-95 transition-transform`}>
                <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                  <Icon size={16} />
                </div>
                <div>
                  <div className="font-bold text-[11px] leading-tight">{f.title}</div>
                  <div className="text-[9px] text-white/80">{f.titleEn}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Teacher Resources Preview */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-gray-900">ଶିକ୍ଷକ ସମ୍ବଳ</h2>
          <Link to="/teacher" className="text-xs font-semibold text-emerald-600 flex items-center gap-0.5">
            ସବୁ ଦେଖନ୍ତୁ <ChevronRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-2.5 mt-3">
          {teacherFeatures.slice(0, 4).map((f) => {
            const Icon = iconMap[f.icon] || BookOpen;
            return (
              <Link key={f.id} to="/teacher"
                className={`relative overflow-hidden rounded-2xl p-3.5 text-white shadow-sm bg-gradient-to-br ${f.gradient} min-h-[110px] flex flex-col justify-between hover:scale-[1.03] active:scale-95 transition-transform`}>
                {f.badge && <span className="absolute top-2 right-2 text-[9px] font-bold bg-white text-gray-800 px-1.5 py-0.5 rounded-full">{f.badge}</span>}
                <div className="w-9 h-9 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                  <Icon size={17} />
                </div>
                <div>
                  <div className="font-bold text-[13px] leading-tight">{f.title}</div>
                  <div className="text-[10px] text-white/85">{f.titleEn}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Testimonials */}
      <div>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-gray-900 flex items-center gap-1.5">
            <MessageCircle size={16} className="text-emerald-500" /> ମତାମତ · Reviews
          </h2>
          <div className="flex items-center gap-1 text-xs">
            <div className="flex text-amber-400">{[...Array(5)].map((_, i) => <Star key={i} size={11} className="fill-amber-400" />)}</div>
            <span className="font-bold text-gray-800">5.0</span>
          </div>
        </div>

        <div className="flex gap-3 mt-3 overflow-x-auto snap-x no-scrollbar -mx-4 px-4 pb-2">
          {testimonials.map((t, i) => (
            <div key={i} className="shrink-0 w-72 snap-start bg-white rounded-2xl p-4 border border-gray-200 shadow-sm">
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-full ${t.color} text-white flex items-center justify-center font-bold`}>
                  {t.initial}
                </div>
                <div>
                  <div className="font-semibold text-sm text-gray-900">{t.name}</div>
                  <div className="text-[10px] text-gray-500">{t.role}</div>
                </div>
              </div>
              <div className="flex text-amber-400 mt-2">
                {[...Array(t.rating)].map((_, j) => <Star key={j} size={11} className="fill-amber-400" />)}
              </div>
              <p className="text-xs text-gray-600 mt-2 leading-relaxed">“{t.quote}”</p>
            </div>
          ))}
        </div>
      </div>

      {/* Premium CTA */}
      <div className="relative overflow-hidden rounded-3xl p-5 text-white shadow-lg"
           style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 60%, #ef4444 100%)' }}>
        <div className="absolute -top-6 -right-6 opacity-20">
          <Trophy size={100} />
        </div>
        <Trophy size={26} className="mb-2" />
        <h3 className="text-xl font-extrabold">ପ୍ରିମିୟମ୍ ପ୍ରବେଶ ନିଅନ୍ତୁ</h3>
        <p className="text-xs text-white/95 mt-1">ସମସ୍ତ ପାଠ୍ୟ, ମକ୍ ଟେଷ୍ଟ ଓ ମଡେଲ୍ ପେପର — ମାତ୍ର ₹{minPrice}/ମାସ ରୁ ଆରମ୍ଭ</p>
        <Link to="/payment">
          <button className="mt-3 bg-white text-orange-600 font-bold px-4 py-2 rounded-full text-sm flex items-center gap-1.5 hover:bg-white/90 transition-colors">
            Go Premium <ChevronRight size={14} />
          </button>
        </Link>
      </div>

      {/* Install App */}
      <Card className="rounded-2xl p-4 border-gray-200">
        <div className="flex items-start gap-3">
          <Download size={22} className="text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-gray-900">Play Store ରୁ Install କରନ୍ତୁ</div>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed">
              Chrome (Android) କିମ୍ବା Safari (iPhone) ରେ ଏହି ସାଇଟ ଖୋଲନ୍ତୁ · Install ବିକଳ୍ପ ସ୍ୱୟଂ ଆସିବ।
            </p>
          </div>
        </div>
      </Card>

      {/* Footer */}
      <Card className="rounded-2xl p-4 border-gray-200 space-y-3">
        <div className="grid grid-cols-2 gap-2 text-sm">
          <a className="flex items-center gap-1.5 text-gray-700 hover:text-emerald-600 font-medium">
            <GraduationCap size={14} /> ଆମ ବିଷୟରେ
          </a>
          <a className="flex items-center gap-1.5 text-gray-700 hover:text-emerald-600 font-medium">
            <Shield size={14} /> Privacy Policy
          </a>
          <a className="flex items-center gap-1.5 text-gray-700 hover:text-emerald-600 font-medium">
            <ScrollText size={14} /> Terms & Conditions
          </a>
          <a className="flex items-center gap-1.5 text-gray-700 hover:text-emerald-600 font-medium">
            <Wallet size={14} /> Refund Policy
          </a>
        </div>
        <div className="border-t border-gray-100 pt-3 space-y-2 text-xs">
          <a href="mailto:gurusevakendra33@gmail.com" className="flex items-center gap-2 text-gray-600 hover:text-emerald-600">
            <Mail size={13} /> gurusevakendra33@gmail.com
          </a>
          <a href="https://wa.me/917325893950" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-gray-600 hover:text-green-600">
            <MessageCircle size={13} /> WhatsApp: +91 73258 93950
          </a>
        </div>
      </Card>

      <div className="text-center text-xs text-gray-500 pt-1">
        <div>🇮🇳 ଓଡ଼ିଶାର ଛାତ୍ର ଓ ଶିକ୍ଷକଙ୍କ ପାଇଁ <span className="text-red-500">♥</span> ସହିତ ନିର୍ମିତ</div>
        <div className="mt-1">© 2025 Gurusevakendra · All rights reserved</div>
      </div>
    </div>
  );
}
